import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
`;

const BackButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const SongTitle = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  text-align: center;
`;

const PrintControls = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  gap: 2rem;
`;

const ControlSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  text-align: center;
`;

const ViewPDFButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  
  &:hover {
    background: #0052a3;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrintSettings = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SettingGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Label = styled.label`
  color: #cccccc;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const NumberInput = styled.input`
  width: 100px;
  padding: 0.75rem;
  border: 2px solid #444;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 1.2rem;
  text-align: center;
  font-weight: 700;
  
  &:focus {
    border-color: #cc0000;
    outline: none;
  }
`;

const AmountControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-top: 1rem;
`;

const AmountButton = styled.button`
  background: ${props => props.primary ? '#cc0000' : '#444'};
  color: white;
  border: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? '#aa0000' : '#555'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AmountDisplay = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  min-width: 120px;
  text-align: center;
`;

const ConfirmButton = styled.button`
  background: #00aa00;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  
  &:hover {
    background: #008800;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrintedAmountLabel = styled.div`
  color: #cccccc;
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #cc0000;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  text-align: center;
  min-width: 120px;
`;

const InfoLabel = styled.div`
  color: #cccccc;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  color: #ffffff;
  font-weight: 700;
  font-size: 1.6rem;
`;

const InfoHint = styled.div`
  color: #888;
  font-size: 0.75rem;
  margin-top: 0.15rem;
`;

function PrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentAmount, setCurrentAmount] = useState(0);
  const [targetAmount, setTargetAmount] = useState(0);
  const [hasPDF, setHasPDF] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);

  useEffect(() => {
    loadSong();
  }, [id]);

  const loadSong = async () => {
    try {
      setLoading(true);
      const songData = await songAPI.getSong(id);
      setSong(songData);
      setCurrentAmount(songData.current_amount);
      setTargetAmount(songData.target_amount);
      setHasPDF(!!songData.pdf_filename);
    } catch (error) {
      console.error('Error loading song:', error);
      toast.error('Failed to load song');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = () => {
    try {
      // Open direct URL to PDF to avoid blob handling issues
      window.open(`/api/songs/${id}/pdf`, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF');
    }
  };

  const amountToPrint = Math.max(0, targetAmount - currentAmount);

  const openStackedPrintPdf = (doubleSided = false) => {
    if (amountToPrint === 0) {
      toast('Nothing to print. Already at target.');
      return;
    }
    if (amountToPrint > 50) {
      toast.error('Too many copies to stack (max 50). Use Print and set copies in the printer dialog.');
      return;
    }
    try {
      const qs = doubleSided ? '?doubleSided=1' : '';
      window.open(`/api/songs/${id}/pdf-print-amount${qs}`, '_blank');
    } catch (error) {
      console.error('Error opening stacked print PDF:', error);
      toast.error('Failed to open stacked print PDF');
    }
  };

  const handlePrintAmount = () => openStackedPrintPdf(false);
  const handlePrintAmountDoubleSided = () => openStackedPrintPdf(true);

  const handleAdjustCurrentAmount = async (adjustment) => {
    try {
      const updated = await songAPI.adjustAmount(id, adjustment);
      setCurrentAmount(updated.current_amount);
      // Refresh handed-out when minusing so the counter stays current
      if (adjustment < 0) {
        const songData = await songAPI.getSong(id);
        setSong((prev) => ({
          ...prev,
          ...songData,
          handed_out: songData.handed_out,
          handed_out_since_year: songData.handed_out_since_year
        }));
      }
    } catch (e) {
      toast.error('Failed to update amount');
    }
  };

  const handleConfirmPrinted = async () => {
    // Compute required amount to reach target
    const needed = Math.max(0, targetAmount - currentAmount);
    if (needed === 0) {
      toast('Nothing to print. Already at target.');
      return;
    }

    try {
      setPrinting(true);
      const newAmount = currentAmount + needed;
      await songAPI.updateSong(id, { current_amount: newAmount });
      setCurrentAmount(newAmount);
      toast.success(`Confirmed ${needed} printed. Total: ${newAmount}`);
    } catch (error) {
      console.error('Error confirming printed pages:', error);
      toast.error('Failed to confirm printed pages');
    } finally {
      setPrinting(false);
    }
  };

  const handleAdjustTargetAmount = (adjustment) => {
    setTargetAmount((prev) => Math.max(0, prev + adjustment));
  };

  const handleSaveTargetAmount = async () => {
    try {
      setSavingTarget(true);
      const updated = await songAPI.updateSong(id, { target_amount: targetAmount });
      setSong((prev) => ({ ...prev, ...updated, target_amount: targetAmount }));
      toast.success('Target amount updated');
    } catch (error) {
      console.error('Error updating target amount:', error);
      toast.error('Failed to update target amount');
    } finally {
      setSavingTarget(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!song) {
    return (
      <PageContainer>
        <ErrorMessage>
          <h2>Song not found</h2>
          <p>The song you're looking for doesn't exist.</p>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/advanced?tab=print')}>
        ← Back to Print Overview
      </BackButton>

      <SongTitle>{song.name}</SongTitle>

      <PrintControls>
        <ControlSection>
          <InfoRow>
            <InfoItem>
              <InfoLabel>Handed out</InfoLabel>
              <InfoValue>{song.handed_out ?? 0}</InfoValue>
              <InfoHint>
                since Nov {song.handed_out_since_year ?? '—'}
              </InfoHint>
            </InfoItem>
            <InfoItem>
              <InfoLabel>PDF pages</InfoLabel>
              <InfoValue>
                {song.pdf_page_count != null ? song.pdf_page_count : (hasPDF ? '—' : 'n/a')}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Target</InfoLabel>
              <InfoValue>{targetAmount}</InfoValue>
            </InfoItem>
          </InfoRow>
        </ControlSection>

        {/* Print header with PDF open */}
        <ControlSection>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <ViewPDFButton onClick={handleViewPDF} disabled={!hasPDF}>
              🖨️ Print
            </ViewPDFButton>
            <ViewPDFButton
              onClick={handlePrintAmount}
              disabled={!hasPDF || amountToPrint === 0}
              style={{ background: '#008866' }}
            >
              🖨️ Print Amount ({amountToPrint})
            </ViewPDFButton>
            <ViewPDFButton
              onClick={handlePrintAmountDoubleSided}
              disabled={!hasPDF || amountToPrint === 0}
              style={{ background: '#6644aa' }}
            >
              🖨️ Print Amount Double-Sided ({amountToPrint})
            </ViewPDFButton>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#cccccc' }}>
              <span>Amount to print:</span>
              <span style={{ color: '#ffffff', fontWeight: 700 }}>
                {amountToPrint}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
            <ConfirmButton 
              onClick={handleConfirmPrinted}
              disabled={printing || amountToPrint === 0}
            >
              {printing ? 'Confirming...' : '✓ Confirm Printing'}
            </ConfirmButton>
          </div>
        </ControlSection>

        {/* Printed amount controls */}
        <ControlSection>
          <PrintedAmountLabel>Printed amount:</PrintedAmountLabel>
          <AmountControls>
            <AmountButton
              type="button"
              onClick={() => handleAdjustCurrentAmount(-1)}
            >
              −
            </AmountButton>
            <AmountDisplay>{currentAmount}</AmountDisplay>
            <AmountButton
              type="button"
              primary
              onClick={() => handleAdjustCurrentAmount(1)}
            >
              +
            </AmountButton>
          </AmountControls>
        </ControlSection>

        {/* Target amount — kept at the bottom */}
        <ControlSection>
          <PrintedAmountLabel>Target amount:</PrintedAmountLabel>
          <AmountControls>
            <AmountButton
              type="button"
              onClick={() => handleAdjustTargetAmount(-1)}
            >
              −
            </AmountButton>
            <AmountDisplay>{targetAmount}</AmountDisplay>
            <AmountButton
              type="button"
              primary
              onClick={() => handleAdjustTargetAmount(1)}
            >
              +
            </AmountButton>
          </AmountControls>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.25rem' }}>
            <ConfirmButton
              onClick={handleSaveTargetAmount}
              disabled={savingTarget || targetAmount === song.target_amount}
            >
              {savingTarget ? 'Saving...' : 'Save Target'}
            </ConfirmButton>
          </div>
        </ControlSection>

      </PrintControls>
    </PageContainer>
  );
}

export default PrintPage;
